import { Box, Button, Checkbox, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Radio, RadioGroup, Select, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useMemo, useState } from "react";
import { adminDeleteComments, adminGetComments, adminUpdateCommentHiddenReason, adminUpdateCommentStatus } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import TextModal from "../../../components/Modals/TextModal";
import { colors } from "../../../styles";
import { IAdminComment, IAdminCommentList } from "../../../types";
import { formatDateTimeKor } from "../../../utils";

export default function Comments() {

    const [page, setPage] = useState(1);
    const size = 100;
    const [isReportedOnly, setIsReportedOnly] = useState<string>("all");
    const [isHiddenOnly, setIsHiddenOnly] = useState<string>("all");

    const reportedOnlyParam = useMemo(() => {
        if (isReportedOnly === "all") return undefined;
        return isReportedOnly === "true";
    }, [isReportedOnly]);

    const hiddenOnlyParam = useMemo(() => {
        if (isHiddenOnly === "all") return undefined;
        return isHiddenOnly === "true";
    }, [isHiddenOnly]);

    const handleIsReportedOnlyChange = (value: string) => {
        setPage(1);
        setIsReportedOnly(value);
    };

    const handleIsHiddenOnlyChange = (value: string) => {
        setPage(1);
        setIsHiddenOnly(value);
    };

    const { data: commentListData, isLoading: isCommentListDataLoading } = useQuery<IAdminCommentList>({
        queryKey: ['adminGetComments', page, size, reportedOnlyParam, hiddenOnlyParam],
        queryFn: () => adminGetComments({ page, size, isReportedOnly: reportedOnlyParam, isHiddenOnly: hiddenOnlyParam }),
        enabled: true,
    });

    const totalPages = useMemo(() => commentListData?.totalPages ?? 0, [commentListData]);
    const currentPage = useMemo(() => commentListData?.currentPage ?? 0, [commentListData]);

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    const commentColumns = [
        { key: "chat.profile.nickname", label: "📄👤 원글 작성자" },
        { key: "chat.content", label: "📄 원글 내용" },
        { key: "profile.nickname", label: "💬👤 댓글 작성자" },
        { key: "content", label: "💬 댓글 내용" },
        { key: "reports", label: "신고 수" },
        { key: "status", label: "상태" },
        { key: "hiddenReason", label: "숨김 사유" },
        { key: "createdAt", label: "생성 일자" },
        { key: "updatedAt", label: "수정 일자" },
    ];

    const [selectedCommentIds, setSelectedCommentIds] = useState<string[]>([]);
    const currentPageCommentIds = useMemo(() => commentListData?.comments.map((comment) => comment.id) ?? [], [commentListData]);

    const isAllCurrentPageSelected = useMemo(() => {
        return currentPageCommentIds.length > 0 && currentPageCommentIds.every((id) => selectedCommentIds.includes(id));
    }, [currentPageCommentIds, selectedCommentIds]);

    const isSomeCurrentPageSelected = useMemo(() => {
        const someSelected = currentPageCommentIds.some((id) => selectedCommentIds.includes(id));
        return someSelected && !isAllCurrentPageSelected;
    }, [currentPageCommentIds, selectedCommentIds, isAllCurrentPageSelected]);

    const toggleSelectAllCurrentPage = () => {
        if (isAllCurrentPageSelected) {
            setSelectedCommentIds((prev) => prev.filter((id) => !currentPageCommentIds.includes(id)));
        } else {
            setSelectedCommentIds((prev) => Array.from(new Set([...prev, ...currentPageCommentIds])));
        }
    };

    const toggleSelectOne = (commentId: string) => {
        setSelectedCommentIds((prev) => (prev.includes(commentId) ? prev.filter((id) => id !== commentId) : [...prev, commentId]));
    };

    const { isOpen: isDeleteModalOpen, onOpen: onOpenDeleteModal, onClose: onCloseDeleteModal } = useDisclosure();

    const queryClient = useQueryClient();
    const toast = useToast();

    const deleteCommentsMutation = useMutation({
        mutationFn: adminDeleteComments,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetComments'] });
            setSelectedCommentIds([]);
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "댓글 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const onDeleteComments = () => {
        deleteCommentsMutation.mutate({ commentIds: selectedCommentIds });
        onCloseDeleteModal();
    };

    const { isOpen: isTextModalOpen, onOpen: onOpenTextModal, onClose: onCloseTextModal } = useDisclosure();
    const [selectedText, setSelectedText] = useState<string>("");

    const openText = (text: string) => {
        setSelectedText(text);
        onOpenTextModal();
    };

    const closeText = () => {
        setSelectedText("");
        onCloseTextModal();
    };

    const updateCommentStatusMutation = useMutation({
        mutationFn: adminUpdateCommentStatus,
        onMutate: async (variables) => {
            const { commentId, status } = variables;
            await queryClient.cancelQueries({ queryKey: ['adminGetComments', page, size, reportedOnlyParam, hiddenOnlyParam] });
            const previous = queryClient.getQueryData<IAdminCommentList>(['adminGetComments', page, size, reportedOnlyParam, hiddenOnlyParam]);
            if (previous) {
                const next = {
                    ...previous,
                    comments: previous.comments.map((c) => (
                        c.id === commentId
                            ? {
                                ...c,
                                status,
                                hiddenAt: status === "HIDDEN" ? (c.hiddenAt ?? new Date()) : null,
                            }
                            : c
                    )),
                };
                queryClient.setQueryData(['adminGetComments', page, size, reportedOnlyParam, hiddenOnlyParam], next);
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            const prev = (context as { previous?: IAdminCommentList })?.previous;
            if (prev) {
                queryClient.setQueryData(['adminGetComments', page, size, reportedOnlyParam, hiddenOnlyParam], prev);
            }
            toast({
                title: "상태 변경에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const updateCommentHiddenReasonMutation = useMutation({
        mutationFn: adminUpdateCommentHiddenReason,
        onMutate: async (variables) => {
            const { commentId, hiddenReason } = variables;
            await queryClient.cancelQueries({ queryKey: ['adminGetComments', page, size, reportedOnlyParam, hiddenOnlyParam] });
            const previous = queryClient.getQueryData<IAdminCommentList>(['adminGetComments', page, size, reportedOnlyParam, hiddenOnlyParam]);
            if (previous) {
                const next = {
                    ...previous,
                    comments: previous.comments.map((c) => (
                        c.id === commentId ? { ...c, hiddenReason } : c
                    )),
                };
                queryClient.setQueryData(['adminGetComments', page, size, reportedOnlyParam, hiddenOnlyParam], next);
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            const prev = (context as { previous?: IAdminCommentList })?.previous;
            if (prev) {
                queryClient.setQueryData(['adminGetComments', page, size, reportedOnlyParam, hiddenOnlyParam], prev);
            }
            toast({
                title: "숨김 사유 저장에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    type CommentStatus = IAdminComment["status"];

    const statusDotColor = (status: CommentStatus) => {
        if (status === "HIDDEN") return colors.apple;
        if (status === "HOLD") return colors.yellow;
        return colors.green;
    };

    const statusComponent = (commentId: string, status: CommentStatus) => {
        return (
            <HStack justifyContent={"flex-start"} alignItems={"center"} width={"180px"} gap={2}>
                <Box
                    width={"8px"}
                    height={"8px"}
                    borderRadius={"4px"}
                    backgroundColor={statusDotColor(status)}
                />
                <Select
                    size="xs"
                    value={status}
                    onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                        const nextStatus = event.target.value as CommentStatus;
                        if (nextStatus !== status) {
                            updateCommentStatusMutation.mutate({ commentId, status: nextStatus });
                        }
                    }}
                    isDisabled={updateCommentStatusMutation.isPending}
                >
                    <option value="NORMAL">NORMAL</option>
                    <option value="HOLD">HOLD</option>
                    <option value="HIDDEN">HIDDEN</option>
                </Select>
            </HStack>
        );
    };

    function HiddenReasonCell({ comment }: { comment: IAdminComment }) {
        const [isEditing, setIsEditing] = useState(false);
        const [value, setValue] = useState<string>(String(comment.hiddenReason ?? ""));
        const canEditHiddenReason = comment.status === "HIDDEN";

        return (
            <HStack justifyContent={"space-between"} alignItems={"center"} width={"100%"} gap={2}>
                {canEditHiddenReason && isEditing ? (
                    <>
                        <Input
                            flex={"1"}
                            minW={"320px"}
                            size={"sm"}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                        />
                        <HStack flexShrink={0} gap={1}>
                            <Button
                                size={"xs"}
                                variant={"solid"}
                                backgroundColor={colors.gray}
                                borderRadius={10}
                                _hover={{ bg: colors.blue }}
                                boxShadow="sm"
                                onClick={async () => {
                                    await updateCommentHiddenReasonMutation.mutateAsync({ commentId: comment.id, hiddenReason: value });
                                    setIsEditing(false);
                                }}
                            >
                                <Text fontSize={8} fontWeight={400} color={colors.white}>💾</Text>
                            </Button>
                            <Button
                                size={"xs"}
                                variant={"ghost"}
                                borderRadius={10}
                                onClick={() => {
                                    setValue(String(comment.hiddenReason ?? ""));
                                    setIsEditing(false);
                                }}
                            >
                                <Text fontSize={8} fontWeight={400} color={colors.black}>❌</Text>
                            </Button>
                        </HStack>
                    </>
                ) : (
                    <>
                        <Text flex={"1"} minW={"200px"} noOfLines={1} color={canEditHiddenReason ? undefined : colors.g3}>
                            {String(comment.hiddenReason ?? "-")}
                        </Text>
                        <HStack flexShrink={0} gap={1}>
                            <Button
                                size={"xs"}
                                variant={"solid"}
                                backgroundColor={colors.white}
                                borderRadius={10}
                                _hover={{ bg: canEditHiddenReason ? colors.blue : colors.white }}
                                boxShadow="sm"
                                isDisabled={!canEditHiddenReason}
                                onClick={() => {
                                    setValue(String(comment.hiddenReason ?? ""));
                                    setIsEditing(true);
                                }}
                            >
                                <Text fontSize={12} fontWeight={400} color={canEditHiddenReason ? colors.black : colors.g3}>🖋️</Text>
                            </Button>
                        </HStack>
                    </>
                )}
            </HStack>
        );
    }

    const renderCellValue = (comment: IAdminComment, columnKey: string) => {
        if (columnKey === "profile.nickname") {
            return String(comment.profile?.nickname ?? "-");
        }
        if (columnKey === "chat.profile.nickname") {
            return String(comment.chat?.profile?.nickname ?? "-");
        }
        if (columnKey === "chat.content") {
            const content = comment.chat?.content ?? "-";
            const displayContent = content.length > 20 ? `${content.slice(0, 20)}...` : content;
            return (
                <Text cursor={"pointer"} onClick={() => openText(content)}>
                    {displayContent}
                </Text>
            );
        }
        if (columnKey === "reports") {
            return String(comment.reports.length);
        }
        if (columnKey === "content") {
            const content = comment.content ?? "-";
            const displayContent = content.length > 20 ? `${content.slice(0, 20)}...` : content;
            return (
                <Text cursor={"pointer"} onClick={() => openText(content)}>
                    {displayContent}
                </Text>
            );
        }
        if (columnKey === "status") {
            return statusComponent(comment.id, comment.status);
        }
        if (columnKey === "hiddenReason") {
            return <HiddenReasonCell comment={comment} />;
        }
        if (columnKey === "createdAt" || columnKey === "updatedAt") {
            return formatDateTimeKor(comment[columnKey]);
        }
        return String(comment[columnKey as keyof IAdminComment] ?? "-");
    };

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>댓글</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{commentListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3} />
                </HStack>

                <RadioGroup value={isReportedOnly} onChange={handleIsReportedOnlyChange}>
                    <HStack spacing={6} marginTop={4}>
                        <Box
                            paddingX={3}
                            paddingY={1}
                            backgroundColor={colors.light}
                            borderRadius={10}

                            width={"130px"}
                            textAlign={"center"}
                        >
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>REPORTEDONLY</Text>
                        </Box>
                        <Radio value="all" colorScheme="blue">모두</Radio>
                        <Radio value="true" colorScheme="blue">TRUE</Radio>
                        <Radio value="false" colorScheme="blue">FALSE</Radio>
                    </HStack>
                </RadioGroup>

                <RadioGroup value={isHiddenOnly} onChange={handleIsHiddenOnlyChange}>
                    <HStack spacing={6} marginBottom={4}>
                        <Box
                            paddingX={3}
                            paddingY={1}
                            backgroundColor={colors.light}
                            borderRadius={10}

                            width={"130px"}
                            textAlign={"center"}
                        >
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>HIDDENEDONLY</Text>
                        </Box>
                        <Radio value="all" colorScheme="blue">모두</Radio>
                        <Radio value="true" colorScheme="blue">TRUE</Radio>
                        <Radio value="false" colorScheme="blue">FALSE</Radio>
                    </HStack>
                </RadioGroup>

                <HStack justifyContent={"flex-start"} marginY={0}>
                    <Button
                        size={"sm"}
                        variant={"solid"}
                        isDisabled={selectedCommentIds.length === 0}
                        opacity={selectedCommentIds.length === 0 ? 0.1 : 1}
                        onClick={onOpenDeleteModal}
                    >
                        <MyIcon src={"/svg/trash-filled.svg"} alt="trash" width={16} height={16} />
                    </Button>
                    <Modal isOpen={isDeleteModalOpen} onClose={onCloseDeleteModal}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>
                                <Text fontSize={20} fontWeight={700} color={colors.black}>삭제하기</Text>
                                <ModalCloseButton />
                            </ModalHeader>
                            <ModalBody>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>선택된 행 수: {selectedCommentIds.length}</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>정말 삭제하시겠습니까?</Text>
                            </ModalBody>
                            <Stack marginTop={5} width={"100%"} padding={3}>
                                <Button
                                    onClick={onDeleteComments}
                                    isLoading={deleteCommentsMutation.isPending}
                                    width={"100%"}
                                    backgroundColor={colors.blue}
                                    color={colors.white}
                                    borderRadius={10}
                                    _hover={{ bg: colors.blue }}
                                    boxShadow="sm"
                                >
                                    삭제
                                </Button>
                            </Stack>
                        </ModalContent>
                    </Modal>
                </HStack>

                {!isCommentListDataLoading ? (
                    <TableContainer marginTop={5}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    <Th>
                                        <Checkbox
                                            isChecked={isAllCurrentPageSelected}
                                            isIndeterminate={isSomeCurrentPageSelected}
                                            onChange={toggleSelectAllCurrentPage}
                                        />
                                    </Th>
                                    {commentColumns.map((column) => (
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {commentListData?.comments.map((comment) => (
                                    <Tr key={comment.id}>
                                        <Td>
                                            <Checkbox
                                                isChecked={selectedCommentIds.includes(comment.id)}
                                                onChange={() => toggleSelectOne(comment.id)}
                                            />
                                        </Td>
                                        {commentColumns.map((column) => (
                                            <Td key={column.key}>
                                                {renderCellValue(comment, column.key)}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}

                <HStack justifyContent={"center"} marginTop={5}>
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}

                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}

                    {pages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(pageNumber)}
                            backgroundColor={pageNumber === currentPage ? colors.blue : ""}
                            color={pageNumber === currentPage ? colors.white : ""}
                        >
                            {pageNumber}
                        </Button>
                    ))}

                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}

                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>

            <TextModal
                isOpen={isTextModalOpen}
                onClose={closeText}
                text={selectedText}
            />
        </AdminOnly>
    );
}
